'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { completeOnboarding } from '@/services/onboardingService';
import type { CoachName } from '@/types/institutional';

const TOTAL_STEPS = 5; // 0: bienvenida, 1: negocio, 2: objetivo, 3: coach, 4: aide-confirm

const COACH_OPTIONS: Array<{
  id: CoachName;
  name: string;
  tagline: string;
  description: string;
  accent: string;
  route: string;
  icon: string;
}> = [
  {
    id: 'gen-ia',
    name: 'Gen-IA',
    tagline: 'Tengo una idea y quiero explorarla.',
    description: 'Lluvia de ideas sin presión, con contexto y primeros pasos.',
    accent: 'from-cyan-500 to-blue-500',
    route: '/brainstorm-coach',
    icon: 'SparklesIcon',
  },
  {
    id: 'socriates',
    name: 'SocrIAtes',
    tagline: 'Quiero cuestionar mi enfoque.',
    description: 'Preguntas difíciles que fortalecen tu idea antes de apostar por ella.',
    accent: 'from-fuchsia-500 to-purple-600',
    route: '/obx',
    icon: 'AcademicCapIcon',
  },
  {
    id: 'aide',
    name: 'AIde',
    tagline: 'Tengo algo armado y quiero mejorarlo.',
    description: 'Convierte lo que ya tienes en un proyecto concreto y accionable.',
    accent: 'from-amber-500 to-orange-500',
    route: '/ai-idea-coach',
    icon: 'WrenchScrewdriverIcon',
  },
];

interface Props {
  userId: string;
  onSkip?: () => void;
}

export default function GeneralEntrepreneurOnboarding({ userId, onSkip }: Props) {
  const router = useRouter();
  const [step, setStep] = useState(0);
  const [businessDescription, setBusinessDescription] = useState('');
  const [mainObjective, setMainObjective] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [pendingAide, setPendingAide] = useState(false);

  const buildParams = () => {
    const params = new URLSearchParams();
    if (businessDescription.trim()) params.set('business', businessDescription.trim());
    if (mainObjective.trim()) params.set('objective', mainObjective.trim());
    params.set('from', 'onboarding');
    return params;
  };

  const handleSelectCoach = async (coach: (typeof COACH_OPTIONS)[number]) => {
    // For AIde: show confirmation step instead of redirecting immediately
    if (coach.id === 'aide') {
      setPendingAide(true);
      setStep(4);
      return;
    }

    setIsSaving(true);
    try {
      await completeOnboarding(userId, {
        business_description: businessDescription.trim(),
        main_objective: mainObjective.trim(),
        first_selected_coach: coach.id,
      });
    } catch {
      // Error silencioso — el onboarding no bloquea el acceso
    }

    router.push(`${coach.route}?${buildParams().toString()}`);
  };

  const handleConfirmAide = async () => {
    setIsSaving(true);
    try {
      await completeOnboarding(userId, {
        business_description: businessDescription.trim(),
        main_objective: mainObjective.trim(),
        first_selected_coach: 'aide',
      });
    } catch {
      // Error silencioso
    }
    router.push(`/ai-idea-coach?${buildParams().toString()}`);
  };

  const canAdvanceStep1 = step === 1 && businessDescription.trim().length >= 3;
  const canAdvanceStep2 = step === 2 && mainObjective.trim().length >= 3;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">

        {/* Barra de progreso */}
        <div className="h-1 bg-gray-100">
          <div
            className="h-full bg-gradient-to-r from-[#2c78fb] to-[#3ee0be] transition-all duration-500"
            style={{ width: step === 4 ? '100%' : `${((step + 1) / (TOTAL_STEPS - 1)) * 100}%` }}
          />
        </div>

        <div className="p-7 sm:p-8">

          {/* PASO 0: Bienvenida */}
          {step === 0 && (
            <div className="text-center space-y-5">
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto shadow-lg"
                style={{ backgroundImage: 'linear-gradient(135deg, #2c78fb 0%, #3ee0be 100%)' }}
              >
                <Icon name="SparklesIcon" size={32} className="text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-black text-gray-900 mb-2">
                  Bienvenido a LynMet
                </h2>
                <p className="text-sm font-semibold text-[#2c78fb] uppercase tracking-wider mb-4">
                  Tu equipo de coaches IA
                </p>
                <p className="text-gray-600 leading-relaxed">
                  LynMet te ayuda a <strong>ordenar tus ideas</strong>, cuestionar tu
                  estrategia y convertir tus objetivos en acciones concretas usando
                  coaches de inteligencia artificial.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-3 pt-2">
                {[
                  { icon: 'LightBulbIcon', label: 'Explora ideas' },
                  { icon: 'AcademicCapIcon', label: 'Cuestiona enfoques' },
                  { icon: 'RocketLaunchIcon', label: 'Arma proyectos' },
                ].map(({ icon, label }) => (
                  <div key={label} className="bg-gray-50 rounded-xl p-3 text-center">
                    <Icon name={icon as any} size={22} className="text-[#2c78fb] mx-auto mb-1" />
                    <span className="text-xs text-gray-600 font-medium">{label}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => setStep(1)}
                className="w-full py-3.5 px-6 rounded-xl font-bold text-white text-base shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg"
                style={{ backgroundImage: 'linear-gradient(135deg, #2c78fb 0%, #3ee0be 100%)' }}
              >
                Siguiente
                <Icon name="ArrowRightIcon" size={18} className="inline ml-2 -mt-0.5" />
              </button>
            </div>
          )}

          {/* PASO 1: Negocio */}
          {step === 1 && (
            <div className="space-y-5">
              <div>
                <p className="text-xs font-semibold text-[#2c78fb] uppercase tracking-wider mb-1">
                  Paso 1 de 3
                </p>
                <h2 className="text-xl font-black text-gray-900">¿Cuál es tu negocio?</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Una descripción breve — con eso los coaches te dan respuestas más útiles.
                </p>
              </div>
              <textarea
                value={businessDescription}
                onChange={(e) => setBusinessDescription(e.target.value)}
                placeholder="Ej: Vendo ropa de segunda mano en Instagram y quiero formalizarlo..."
                rows={4}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#2c78fb] focus:border-transparent transition"
                autoFocus
              />
              <div className="flex gap-3">
                <button
                  onClick={() => setStep(0)}
                  className="flex-1 py-3 px-4 rounded-xl font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition text-sm"
                >
                  Atrás
                </button>
                <button
                  onClick={() => setStep(2)}
                  disabled={!canAdvanceStep1}
                  className="flex-1 py-3 px-4 rounded-xl font-bold text-white text-sm shadow-md transition-all hover:-translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                  style={{ backgroundImage: 'linear-gradient(135deg, #2c78fb 0%, #3ee0be 100%)' }}
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}

          {/* PASO 2: Objetivo */}
          {step === 2 && (
            <div className="space-y-5">
              <div>
                <p className="text-xs font-semibold text-[#2c78fb] uppercase tracking-wider mb-1">
                  Paso 2 de 3
                </p>
                <h2 className="text-xl font-black text-gray-900">
                  ¿Cuál es tu objetivo principal hoy?
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  ¿Qué quieres resolver, mejorar o avanzar con LynMet?
                </p>
              </div>
              <textarea
                value={mainObjective}
                onChange={(e) => setMainObjective(e.target.value)}
                placeholder="Ej: Quiero saber si mi idea tiene mercado antes de invertir más tiempo..."
                rows={4}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#2c78fb] focus:border-transparent transition"
                autoFocus
              />
              <div className="flex gap-3">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 py-3 px-4 rounded-xl font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition text-sm"
                >
                  Atrás
                </button>
                <button
                  onClick={() => setStep(3)}
                  disabled={!canAdvanceStep2}
                  className="flex-1 py-3 px-4 rounded-xl font-bold text-white text-sm shadow-md transition-all hover:-translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                  style={{ backgroundImage: 'linear-gradient(135deg, #2c78fb 0%, #3ee0be 100%)' }}
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}

          {/* PASO 3: Selección de coach */}
          {step === 3 && (
            <div className="space-y-5">
              <div>
                <p className="text-xs font-semibold text-[#2c78fb] uppercase tracking-wider mb-1">
                  Paso 3 de 3
                </p>
                <h2 className="text-xl font-black text-gray-900">¿Con qué quieres empezar?</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Después podrás usar todos los coaches libremente.
                </p>
              </div>
              <div className="space-y-3">
                {COACH_OPTIONS.map((coach) => (
                  <button
                    key={coach.id}
                    onClick={() => handleSelectCoach(coach)}
                    disabled={isSaving}
                    className="w-full text-left p-4 rounded-xl border-2 border-gray-100 hover:border-[#2c78fb]/40 bg-white hover:bg-blue-50/40 transition-all group disabled:opacity-50"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${coach.accent} flex items-center justify-center flex-shrink-0 mt-0.5 shadow`}>
                        <Icon name={coach.icon as any} size={18} className="text-white" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="font-bold text-gray-900 text-sm">{coach.name}</span>
                          <span className="text-xs text-gray-500 italic">"{coach.tagline}"</span>
                        </div>
                        <p className="text-xs text-gray-500 leading-relaxed">{coach.description}</p>
                      </div>
                      <Icon
                        name="ChevronRightIcon"
                        size={16}
                        className="text-gray-300 group-hover:text-[#2c78fb] flex-shrink-0 mt-1 transition-colors"
                      />
                    </div>
                  </button>
                ))}
              </div>
              <button
                onClick={() => setStep(2)}
                className="text-xs text-gray-400 hover:text-gray-600 transition-colors w-full text-center"
              >
                ← Volver al objetivo
              </button>
            </div>
          )}

          {/* PASO 4: Confirmación AIde */}
          {step === 4 && (
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0 shadow">
                  <Icon name="WrenchScrewdriverIcon" size={20} className="text-white" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-[#2c78fb] uppercase tracking-wider">Casi listo</p>
                  <h2 className="text-xl font-black text-gray-900">Comenzar con AIde</h2>
                </div>
              </div>

              <p className="text-sm text-gray-500">
                AIde usa las respuestas que ya diste. Revisa que estén bien antes de empezar.
              </p>

              {/* Steps ya completados */}
              <div className="space-y-2">
                {[
                  { num: 1, label: 'Tu negocio', value: businessDescription },
                  { num: 2, label: 'Tu objetivo', value: mainObjective },
                ].map(({ num, label, value }) => (
                  <div key={num} className="flex items-start gap-3 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-500 text-white text-xs flex items-center justify-center font-bold mt-0.5">✓</span>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-emerald-700">{label}</p>
                      <p className="text-sm text-gray-700 line-clamp-2">{value}</p>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-xs text-gray-400 bg-gray-50 rounded-xl px-4 py-3 leading-relaxed">
                AIde usará estos datos como <strong>título</strong> y <strong>descripción</strong> de tu primera sesión. Podrás editarlos dentro del coach.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => { setPendingAide(false); setStep(3); }}
                  className="flex-1 py-3 px-4 rounded-xl font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition text-sm"
                >
                  Volver
                </button>
                <button
                  onClick={handleConfirmAide}
                  disabled={isSaving}
                  className="flex-1 py-3 px-4 rounded-xl font-bold text-white text-sm shadow-md transition-all hover:-translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ backgroundImage: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)' }}
                >
                  {isSaving ? 'Iniciando...' : 'Comenzar con AIde →'}
                </button>
              </div>
            </div>
          )}

          {/* Skip link — siempre visible excepto en el paso de coaches */}
          {step < 3 && onSkip && (
            <button
              onClick={onSkip}
              className="mt-4 text-xs text-gray-400 hover:text-gray-500 transition-colors w-full text-center"
            >
              Omitir por ahora
            </button>
          )}

        </div>
      </div>
    </div>
  );
}
